import { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { UdyogLogo } from "@/components/UdyogLogo";
import { JoinScreen } from "@/components/JoinScreen";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, MicOff, ShieldCheck, X } from "lucide-react";
import { bannerUrl } from "@/lib/banners";
import { nameRejection, normalizeIndianMobile } from "@/lib/joinValidation";
import { goFullscreenNow } from "@/lib/pwa";
import { getDeviceId } from "@/lib/device";

function useCountdown(target) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!target) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [target]);
  if (!target) return null;
  const diff = new Date(target).getTime() - now;
  if (diff <= 0) return "starting now";
  const d = Math.floor(diff / 86400000);
  const h = Math.floor((diff % 86400000) / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h ${m}m`;
  if (h > 0) return `${h}h ${m}m ${s}s`;
  return `${m}m ${s}s`;
}

export default function Join() {
  const { roomName } = useParams();
  const nav = useNavigate();
  const [meeting, setMeeting] = useState(null);
  const urlParams = new URLSearchParams(window.location.search);
  const urlName = urlParams.get("name") || urlParams.get("n") || "";
  const urlWa = urlParams.get("wa") || urlParams.get("phone") || "";
  // Someone who dropped out (or tapped "Join the class again") must never be
  // asked to fill the form a second time — their details are parked on leave.
  const saved = (() => {
    try {
      const raw = JSON.parse(localStorage.getItem(`udyog_rejoin:${roomName}`) || "null");
      if (raw?.name && Date.now() - (raw.ts || 0) < 8 * 3600 * 1000) return raw;
    } catch (_) {}
    return null;
  })();
  const [name, setName] = useState(urlName || saved?.name || "");
  const [whatsapp, setWhatsapp] = useState(urlWa || saved?.whatsapp || "");
  const [rejoining, setRejoining] = useState(!!saved && !urlName);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [lobbyStatus, setLobbyStatus] = useState("");
  const [banner, setBanner] = useState("brand");
  const autoSubmittedRef = useRef(false);
  const pollRef = useRef(null);
  const countdown = useCountdown(meeting?.schedule_status === "scheduled" ? meeting?.start_at : null);

  useEffect(() => {
    document.title = "Udyog Classes";
    api
      .get("/public/branding")
      .then(({ data }) => setBanner(data?.join_banner || "brand"))
      .catch(() => {});
  }, []);

  useEffect(() => {
    const fetchOnce = async () => {
      try {
        const { data } = await api.get(`/public/meetings/${roomName}`);
        setMeeting(data);
      } catch (e) {
        setErr(formatApiError(e));
      }
    };
    fetchOnce();
    const t = setInterval(fetchOnce, 5000);
    return () => clearInterval(t);
  }, [roomName]);

  const stopLobbyPoll = () => {
    if (pollRef.current) clearInterval(pollRef.current);
    pollRef.current = null;
  };

  const joinFields = (meeting && meeting.join_fields) || "both";
  const needsName = joinFields === "name" || joinFields === "both";
  const needsWhatsapp = joinFields === "whatsapp" || joinFields === "both";
  // Live validation — shown only once the student has started typing.
  const nameError = name.trim().length > 0 ? nameRejection(name) : null;
  const waError =
    whatsapp.replace(/\D/g, "").length >= 4 && !normalizeIndianMobile(whatsapp)
      ? "Enter a valid 10-digit Indian mobile number (starting 6-9)."
      : null;


  // If the host chose "no form" AND the meeting doc has loaded, auto-name
  // this visitor and drop them straight into the room.
  useEffect(() => {
    if (!meeting) return;
    if (joinFields !== "none") return;
    const suffix = Math.floor(1000 + Math.random() * 9000);
    const gname = `Guest ${suffix}`;
    nav(`/meet/${roomName}?name=${encodeURIComponent(gname)}`, { replace: true });
  }, [meeting, joinFields, nav, roomName]);

  const enterMeeting = (finalName, finalWa) => {
    nav(`/meet/${roomName}?name=${encodeURIComponent(finalName)}&wa=${encodeURIComponent(finalWa || "")}`);
  };

  // The host's own phone / laptop is recognised so they never land in the class
  // as a student by accident.
  const [trusted, setTrusted] = useState(null);
  useEffect(() => {
    if (!roomName) return;
    api
      .post("/public/devices/check", { device_id: getDeviceId(), room_name: roomName })
      .then(({ data }) => setTrusted(data))
      .catch(() => setTrusted({ trusted: false }));
  }, [roomName]);

  const joinAs = (role) => {
    goFullscreenNow();
    const who = trusted?.host_name || "Host";
    const q = new URLSearchParams({ name: who });
    if (role === "host") q.set("host", "1");
    else q.set("cohost", "1");
    nav(`/meet/${roomName}?${q.toString()}`);
  };

  const submit = async (e) => {
    e.preventDefault();
    // This tap is our only guaranteed user gesture — spend it on fullscreen so
    // Chrome's address bar is gone for the whole class.
    goFullscreenNow();
    const fail = (msg) => {
      setErr(msg);
      setRejoining(false);
      setLoading(false);
    };
    // Resolve the name — either the one they typed, or a Guest #### fallback
    // when the host isn't asking for one.
    let finalName = name.trim();
    if (needsName) {
      const nameErr = nameRejection(finalName);
      if (nameErr) {
        fail(nameErr);
        return;
      }
    }
    if (!needsName && !finalName) {
      finalName = `Guest ${Math.floor(1000 + Math.random() * 9000)}`;
    }
    // WhatsApp: exactly 10 digits (Indian mobile). +91 / 0 prefixes are stripped.
    if (needsWhatsapp && !normalizeIndianMobile(whatsapp)) {
      fail("Enter a valid 10-digit Indian mobile number (starting 6-9).");
      return;
    }
    setLoading(true);
    setErr("");
    try {
      // One rule, decided by the server: straight in, waiting room, or turned
      // away. A student already admitted to this class keeps their seat even
      // after the host locks the door — only newcomers are stopped.
      const waNum = needsWhatsapp ? whatsapp.trim() : "";
      const { data: gate } = await api.post(`/meetings/${roomName}/gate`, {
        user_name: finalName,
        whatsapp: waNum,
      });
      if (gate.action === "denied") {
        fail("The class is now locked.");
        return;
      }
      if (gate.action === "removed") {
        fail("You were removed from this class.");
        return;
      }
      if (gate.action === "join") {
        enterMeeting(finalName, whatsapp.trim());
        return;
      }
      // Waiting room: knock and wait for the host to admit them.
      await api.post(`/meetings/${roomName}/lobby/knock`, {
        user_name: finalName,
        whatsapp: waNum,
      });
      const check = await api.get(`/meetings/${roomName}/lobby/status`, { params: { user_name: finalName } });
      if (check.data.status === "admitted") {
        enterMeeting(finalName, whatsapp.trim());
        return;
      }
      setWaiting(true);
      setRejoining(false);
      setLobbyStatus("pending");
      pollRef.current = setInterval(async () => {
        try {
          const r = await api.get(`/meetings/${roomName}/lobby/status`, { params: { user_name: finalName } });
          if (r.data.status === "admitted") {
            stopLobbyPoll();
            enterMeeting(finalName, whatsapp.trim());
          } else if (r.data.status === "rejected") {
            stopLobbyPoll();
            setWaiting(false);
            setErr("The host has denied your entry.");
          }
        } catch (_) {}
      }, 2000);
    } catch (e2) {
      setErr(formatApiError(e2));
      setRejoining(false);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => () => stopLobbyPoll(), []);

  // Auto-join if the URL already carries name + WhatsApp (from a WA broadcast
  // link), or if this is a returning student we already know.
  useEffect(() => {
    if (autoSubmittedRef.current) return;
    if (!meeting) return;
    if (meeting.schedule_status !== "active") {
      if (rejoining) setRejoining(false);
      return;
    }
    if (trusted === null) return; // wait: a host device must not auto-join as a student
    if (trusted?.trusted) {
      setRejoining(false);
      return;
    }
    if ((urlName && urlWa) || rejoining) {
      autoSubmittedRef.current = true;
      // fire the same submit path
      submit({ preventDefault() {} });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting, trusted, rejoining]);

  const scheduleStatus = meeting?.schedule_status;
  const blocked = scheduleStatus === "scheduled" || scheduleStatus === "expired" || scheduleStatus === "ended";

  // A returning student sees no form at all — just a moment of "taking you
  // back in" while the server re-checks the lock and the waiting room.
  if (rejoining && !err && !waiting) {
    return (
      <div
        className="min-h-screen bg-[#070908] grid place-items-center px-6"
        data-testid="rejoining-screen"
      >
        <div className="text-center">
          <UdyogLogo size={34} />
          <Loader2 className="w-7 h-7 animate-spin text-emerald-400 mx-auto mt-7" />
          <div className="mt-5 text-white text-base font-semibold">Taking you back in…</div>
          <div className="mt-1.5 text-[13px] text-white/45">
            Welcome back{name ? `, ${name.trim().split(" ")[0]}` : ""} — no need to fill anything again
          </div>
        </div>
      </div>
    );
  }

  // The designed entry screen — used for the normal case. Waiting room,
  // scheduled/closed classes and the "we know this device" prompt keep the
  // detailed card below, because the artwork has no room for them.
  if (meeting && !blocked && !waiting && !trusted?.trusted) {
    return (
      <JoinScreen
        name={name}
        setName={setName}
        whatsapp={whatsapp}
        setWhatsapp={setWhatsapp}
        onSubmit={submit}
        loading={loading}
        needsName={needsName}
        needsWhatsapp={needsWhatsapp}
        error={err}
      />
    );
  }

  return (
    <div className="min-h-screen bg-[#070908] relative overflow-hidden px-4 py-8 sm:py-12">
      <div className="pointer-events-none absolute -top-40 left-[12%] w-[560px] h-[560px] rounded-full bg-emerald-500/[0.10] blur-[140px]" />
      <div className="pointer-events-none absolute -bottom-52 right-[8%] w-[480px] h-[480px] rounded-full bg-emerald-400/[0.06] blur-[140px]" />

      <div className="relative z-10 w-full max-w-[480px] mx-auto">
        <div
          className="rounded-[30px] overflow-hidden bg-[#0b0d0c]/75 backdrop-blur-2xl ring-1 ring-white/[0.08] shadow-[0_34px_90px_-40px_rgba(0,0,0,1)]"
          data-testid="join-card"
        >
          {/* Banner — chosen by the host in the admin dashboard */}
          <div className="relative h-24 sm:h-32">
            <img
              src={bannerUrl(banner)}
              alt=""
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="join-banner"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-[#0b0d0c] via-[#0b0d0c]/35 to-transparent" />
            <div className="absolute left-6 bottom-4">
              <UdyogLogo size={26} />
            </div>
          </div>

          <div className="p-6 sm:p-7">
            {meeting ? (
              <>
                <div className="text-[9.5px] uppercase tracking-[0.26em] text-emerald-400/80 mb-2">
                  {waiting
                    ? "Waiting room"
                    : scheduleStatus === "scheduled"
                    ? "Scheduled"
                    : scheduleStatus === "expired" || scheduleStatus === "ended"
                    ? "Closed"
                    : "Live class"}
                </div>
                <div
                  className="font-display text-white text-xl sm:text-2xl leading-snug tracking-tight mb-6"
                  data-testid="join-meeting-title"
                >
                  {meeting.title || "Udyog Classes"}
                </div>

                {waiting ? (
                  <div className="rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/30 p-4 mb-2" data-testid="lobby-waiting-note">
                    <div className="text-[9.5px] uppercase tracking-[0.2em] text-amber-300 mb-1.5">Please wait</div>
                    <p className="text-sm text-neutral-200">The host will admit you shortly. This page refreshes automatically.</p>
                  </div>
                ) : scheduleStatus === "scheduled" ? (
                  <div className="rounded-2xl bg-amber-500/10 ring-1 ring-amber-500/30 p-4 mb-5" data-testid="meeting-scheduled-note">
                    <div className="text-[9.5px] uppercase tracking-[0.2em] text-amber-300 mb-1.5">Opens in</div>
                    <div className="font-display text-3xl font-bold text-white">{countdown}</div>
                  </div>
                ) : scheduleStatus === "expired" || scheduleStatus === "ended" ? (
                  <div className="rounded-2xl bg-white/[0.03] ring-1 ring-white/[0.07] p-4 text-sm text-neutral-400" data-testid="meeting-ended-note">
                    This meeting is closed.
                  </div>
                ) : null}

                {trusted?.trusted && !waiting && !blocked && (
                  <div
                    className="rounded-2xl bg-emerald-500/[0.07] ring-1 ring-emerald-400/30 p-4 mb-5"
                    data-testid="trusted-device-card"
                  >
                    <div className="text-[9.5px] uppercase tracking-[0.2em] text-emerald-300 mb-1.5">
                      We know this device
                    </div>
                    <p className="text-[13px] text-white/70 mb-3.5">
                      Welcome back{trusted.host_name ? `, ${trusted.host_name.split(" ")[0]}` : ""} — how
                      do you want to join this class?
                    </p>
                    <div className="grid grid-cols-2 gap-2">
                      <button
                        onClick={() => joinAs("host")}
                        className="h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-black text-[11px] uppercase tracking-[0.14em] font-semibold"
                        data-testid="trusted-become-host"
                      >
                        Become host
                      </button>
                      <button
                        onClick={() => joinAs("cohost")}
                        className="h-11 rounded-full ring-1 ring-emerald-400/40 text-emerald-300 hover:bg-emerald-500/10 text-[11px] uppercase tracking-[0.14em] font-semibold"
                        data-testid="trusted-stay-cohost"
                      >
                        Stay co-host
                      </button>
                    </div>
                  </div>
                )}

                {!blocked && !waiting && (
                  <form onSubmit={submit} className="space-y-4" data-testid="join-form">
                    {needsName && (
                      <div>
                        <Label className="text-[13px] sm:text-sm uppercase tracking-[0.14em] text-white font-semibold">
                          Your name
                        </Label>
                        <Input
                          autoFocus
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Aarav Kumar"
                          required
                          minLength={2}
                          maxLength={40}
                          className={`mt-2 h-12 rounded-full px-5 bg-white/[0.04] text-white focus-visible:ring-emerald-500 placeholder:text-white/25 ${
                            nameError ? "border-red-500/60" : "border-white/[0.09]"
                          }`}
                          data-testid="join-name-input"
                        />
                        {nameError && (
                          <div className="mt-2 text-[11.5px] text-red-400" data-testid="join-name-error">
                            {nameError}
                          </div>
                        )}
                      </div>
                    )}
                    {needsWhatsapp && (
                      <div>
                        <Label className="text-[13px] sm:text-sm uppercase tracking-[0.14em] text-white font-semibold">
                          WhatsApp number
                        </Label>
                        <Input
                          type="tel"
                          value={whatsapp}
                          onChange={(e) => setWhatsapp(e.target.value.replace(/[^\d+\s-]/g, "").slice(0, 14))}
                          placeholder="9876543210"
                          required
                          inputMode="numeric"
                          autoFocus={!needsName}
                          className={`mt-2 h-12 rounded-full px-5 bg-white/[0.04] text-white focus-visible:ring-emerald-500 placeholder:text-white/25 ${
                            waError ? "border-red-500/60" : "border-white/[0.09]"
                          }`}
                          data-testid="join-whatsapp-input"
                        />
                        {waError && (
                          <div className="mt-2 text-[11.5px] text-red-400" data-testid="join-whatsapp-error">
                            {waError}
                          </div>
                        )}
                      </div>
                    )}
                    {err && (
                      <div
                        className="relative text-sm text-red-400 bg-red-500/10 ring-1 ring-red-500/30 rounded-2xl pl-4 pr-9 py-2.5"
                        data-testid="join-error"
                      >
                        {err}
                        <button
                          type="button"
                          onClick={() => setErr("")}
                          className="absolute top-1.5 right-1.5 w-6 h-6 grid place-items-center rounded-full text-red-300/70 hover:text-white hover:bg-white/10 transition-colors"
                          aria-label="Dismiss"
                          data-testid="join-error-close"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    )}
                    <Button
                      type="submit"
                      disabled={
                        loading ||
                        (needsName && !!nameRejection(name)) ||
                        (needsWhatsapp && !normalizeIndianMobile(whatsapp))
                      }
                      className="w-full h-13 py-4 rounded-full bg-gradient-to-b from-emerald-400 to-emerald-600 hover:from-emerald-300 hover:to-emerald-500 text-black font-semibold text-[12.5px] tracking-[0.2em] uppercase shadow-[0_14px_40px_-12px_rgba(16,185,129,0.9)]"
                      data-testid="join-submit-btn"
                    >
                      {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : "JOIN CLASS"}
                    </Button>
                    <div className="flex items-center justify-center gap-5 pt-1 text-[11px] text-white/40">
                      <span className="flex items-center gap-1.5">
                        <ShieldCheck className="w-3.5 h-3.5 text-emerald-500" /> No app needed
                      </span>
                      <span className="flex items-center gap-1.5">
                        <MicOff className="w-3.5 h-3.5 text-emerald-500" /> You join muted, camera off
                      </span>
                    </div>
                  </form>
                )}
                {blocked && scheduleStatus === "scheduled" && (
                  <p className="text-xs text-neutral-500 mt-4">This page refreshes automatically once the class opens.</p>
                )}
              </>
            ) : err ? (
              <div className="relative inline-flex items-start gap-2 text-sm text-red-400" data-testid="join-error">
                <span className="pr-1">{err}</span>
                <button
                  type="button"
                  onClick={() => setErr("")}
                  className="mt-0.5 w-5 h-5 shrink-0 grid place-items-center rounded-full text-red-300/70 hover:text-white hover:bg-white/10 transition-colors"
                  aria-label="Dismiss"
                  data-testid="join-error-close"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </div>
            ) : (
              <div className="text-neutral-400 text-sm">Loading class…</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
